import React, { useState, useEffect, useCallback } from 'react';
import { makeClient } from './scheduleClient';
import { ShieldCheck, Shield, MapPin, Plus, Trash2, Users, Mail, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/schedule`;
const axios = makeClient();

const VenueRoleManager = () => {
  const [venues, setVenues] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [venueServices, setVenueServices] = useState({});
  const [loading, setLoading] = useState(true);

  // Assignment form
  const [assignVenue, setAssignVenue] = useState('');
  const [assignCategory, setAssignCategory] = useState('');
  const [assignEmployee, setAssignEmployee] = useState('');
  const [assignType, setAssignType] = useState('');
  const [sendingReport, setSendingReport] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [venuesRes, employeesRes, rolesRes, servicesRes] = await Promise.all([
        axios.get(`${API}/venues`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/venue-roles`),
        axios.get(`${API}/venue-roles/services`)
      ]);
      setVenues(venuesRes.data);
      setEmployees(employeesRes.data);
      setAllRoles(rolesRes.data);
      setVenueServices(servicesRes.data);
    } catch (error) {
      console.error('Error fetching role data:', error);
      toast.error('Failed to load role data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getEmployeeName = (id) => employees.find(e => e.id === id || e.user_id === id)?.name || 'Unknown';

  const handleAssignRole = async () => {
    if (!assignVenue || !assignCategory || !assignEmployee || !assignType) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await axios.post(`${API}/venue-roles`, {
        venue_id: assignVenue,
        employee_id: assignEmployee,
        role_category: assignCategory,
        role_type: assignType
      });
      toast.success('Role assigned!');
      setAssignVenue('');
      setAssignCategory('');
      setAssignEmployee('');
      setAssignType('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign role');
    }
  };

  const handleRemoveRole = async (roleId) => {
    try {
      await axios.delete(`${API}/venue-roles/${roleId}`);
      toast.success('Role removed');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove role');
    }
  };

  // Get available categories based on selected venue - always show both
  const getCategories = () => {
    if (!assignVenue) return [];
    return [
      { value: 'trivia', label: 'Trivia' },
      { value: 'bingo_karaoke', label: 'Bingo/Karaoke' }
    ];
  };

  // Check how many primaries exist and whether the venue allows co-hosting (two primaries)
  const primariesCount = (venueId, category) => {
    return allRoles.filter(r => r.venue_id === venueId && r.role_category === category && r.role_type === 'primary').length;
  };

  const getRoleTypes = () => {
    if (!assignVenue || !assignCategory) return [];
    const venue = venues.find(v => v.id === assignVenue);
    const limit = venue?.allow_two_primaries ? 2 : 1;
    // Only Primary role can be assigned — all other hosts are automatic backups
    if (primariesCount(assignVenue, assignCategory) < limit) {
      return [{ value: 'primary', label: 'Primary' }];
    }
    return []; // Primary slot(s) already taken
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>;
  }

  const categoryLabel = (cat) => cat === 'trivia' ? 'Trivia' : 'Bingo/Karaoke';

  const sendReport = async (which, label) => {
    if (!window.confirm(`Send the ${label} to hosts by email right now?`)) return;
    setSendingReport(true);
    try {
      const res = await axios.post(`${API}/reports/${which}`);
      const n = res.data?.sent ?? 0;
      toast.success(`${label}: ${n} email${n === 1 ? '' : 's'} sent${res.data?.errors?.length ? `, ${res.data.errors.length} failed` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reports');
    } finally { setSendingReport(false); }
  };

  return (
    <div className="space-y-6">
      {/* Email reports (prototype parity: manual send) */}
      <Card data-testid="email-reports-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-lg">
            <Mail className="h-5 w-5" />
            <span>Email Reports</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-400 mb-3">
            These go out automatically — Friday 9am (primary hosts: next week's venue report) and Monday 9am
            (all hosts: open spots this week), Phoenix time. You can also send them now.
          </p>
          <div className="flex flex-wrap gap-3">
            <Button size="sm" disabled={sendingReport} data-testid="send-friday-report-btn"
              onClick={() => sendReport('send-friday', 'Friday primary reports')}>
              <Send className="h-4 w-4 mr-2" /> Send Friday Report Now
            </Button>
            <Button size="sm" variant="outline" disabled={sendingReport} data-testid="send-monday-report-btn"
              onClick={() => sendReport('send-monday', 'Monday open-spots emails')}>
              <Send className="h-4 w-4 mr-2" /> Send Open-Spots Email Now
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Assign Role Form */}
      <Card data-testid="assign-role-card">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2 text-lg">
            <Plus className="h-5 w-5" />
            <span>Assign Primary Host</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Select value={assignVenue} onValueChange={(v) => { setAssignVenue(v); setAssignCategory(''); setAssignType(''); }} data-testid="assign-venue-select">
              <SelectTrigger data-testid="assign-venue-trigger">
                <SelectValue placeholder="Venue" />
              </SelectTrigger>
              <SelectContent>
                {venues.map(v => (
                  <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={assignCategory} onValueChange={(v) => { setAssignCategory(v); setAssignType(''); }} disabled={!assignVenue} data-testid="assign-category-select">
              <SelectTrigger data-testid="assign-category-trigger">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {getCategories().map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={assignType} onValueChange={setAssignType} disabled={!assignCategory} data-testid="assign-type-select">
              <SelectTrigger data-testid="assign-type-trigger">
                <SelectValue placeholder="Role" />
              </SelectTrigger>
              <SelectContent>
                {getRoleTypes().map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={assignEmployee} onValueChange={setAssignEmployee} data-testid="assign-employee-select">
              <SelectTrigger data-testid="assign-employee-trigger">
                <SelectValue placeholder="Employee" />
              </SelectTrigger>
              <SelectContent>
                {employees.filter(e => e.active !== false).map(e => (
                  <SelectItem key={e.id} value={e.id}>{e.name}{!e.linked ? ' (no login)' : ''}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button onClick={handleAssignRole} disabled={!assignVenue || !assignCategory || !assignEmployee || !assignType} data-testid="assign-role-btn">
              Assign
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Venue Roles List - Show ALL venues */}
      {venues.map(venue => {
        const vs = venueServices[venue.id] || {};
        const triviaRoles = allRoles.filter(r => r.venue_id === venue.id && r.role_category === 'trivia');
        const bkRoles = allRoles.filter(r => r.venue_id === venue.id && r.role_category === 'bingo_karaoke');
        const hasTrivia = vs.offers_trivia || triviaRoles.length > 0;
        const hasBK = vs.offers_bingo_karaoke || bkRoles.length > 0;

        return (
          <Card key={venue.id} data-testid={`venue-roles-card-${venue.id}`}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center space-x-2 text-lg">
                <MapPin className="h-5 w-5 text-purple-600" />
                <span>{venue.name}</span>
                {!vs.venue_id && <span className="text-xs text-slate-500 ml-2">(no pricing set)</span>}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <RoleCategorySection
                  title="Trivia"
                  roles={triviaRoles}
                  getEmployeeName={getEmployeeName}
                  onRemove={handleRemoveRole}
                  colorBg="bg-green-50"
                  colorBorder="border-green-200"
                  colorText="text-green-800"
                  badgeColor="bg-green-600"
                />
                <RoleCategorySection
                  title="Bingo/Karaoke"
                  roles={bkRoles}
                  getEmployeeName={getEmployeeName}
                  onRemove={handleRemoveRole}
                  colorBg="bg-pink-50"
                  colorBorder="border-pink-200"
                  colorText="text-pink-800"
                  badgeColor="bg-pink-600"
                />
              </div>
            </CardContent>
          </Card>
        );
      })}

      {venues.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No venues found. Add venues in the Venues tab first.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const RoleCategorySection = ({ title, roles, getEmployeeName, onRemove, colorBg, colorBorder, colorText, badgeColor }) => {
  const primaries = roles.filter(r => r.role_type === 'primary');

  return (
    <div className={`p-4 rounded-lg ${colorBg} border ${colorBorder}`}>
      <h4 className={`font-semibold ${colorText} mb-3`}>{title}</h4>

      {/* Primary (one or two when the venue allows co-hosting) */}
      <div className="mb-3">
        <p className={`text-xs font-semibold ${colorText} uppercase tracking-wider mb-1`}>Primary</p>
        {primaries.length > 0 ? primaries.map(primary => (
          <div key={primary.id} className="flex items-center justify-between p-2 bg-white/5 rounded border border-white/10 mb-1">
            <div className="flex items-center space-x-2">
              <ShieldCheck className="h-4 w-4 text-blue-600" />
              <span className="font-medium text-sm">{getEmployeeName(primary.employee_id)}</span>
            </div>
            <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => onRemove(primary.id)} data-testid={`remove-role-${primary.id}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        )) : (
          <p className="text-sm text-slate-500 italic p-2">Unassigned</p>
        )}
      </div>

      {/* All others are automatic backups */}
      <div className="p-2 bg-white/5 rounded border border-dashed border-white/10">
        <p className="text-xs text-slate-400 italic">All other hosts are automatically notified as backup when this event is unclaimed.</p>
      </div>
    </div>
  );
};

export default VenueRoleManager;
